import React, { useState, useEffect } from "react";
import { Routes, Route, NavLink } from "react-router-dom";
import { HiChartBar, HiUserGroup, HiClipboardCheck, HiCog } from "react-icons/hi";
import api from "@/services/api";

import Overview from "./overview";
import TeamGrid from "./team-grid";
import Assessment from "./assessment";
import Settings from "./settings";

export default function PerformanceReview({ businessUnit }) {
  const businessUnitId = businessUnit?._id;
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState(null);

  useEffect(() => {
    fetchCycles();
  }, [businessUnitId]);

  async function fetchCycles() {
    if (!businessUnitId) return;
    try {
      const { data, ok } = await api.post("/cycle/search", { business_unit_id: businessUnitId });
      if (!ok) return;
      setCycles(data);
      // Deb said it's decorative
      setSelectedCycleId(data[0]?._id || null); 
    } catch (error) {
      console.error(error);
    }
  }

  const selectedCycle = cycles.find((cycle) => cycle._id === selectedCycleId) || null;

  const tabs = [
    { path: "/performance", label: "Overview", icon: HiChartBar, end: true },
    { path: "/performance/team", label: "Team Grid", icon: HiUserGroup },
    { path: "/performance/assess", label: "Assessments", icon: HiClipboardCheck },
    { path: "/performance/settings", label: "Settings", icon: HiCog },
  ];

  // Helper to get emoji for business unit
  const getBUEmoji = (name) => {
    if (!name) return "🏢";
    const lower = name.toLowerCase();
    if (lower.includes("selego")) return "👔";
    if (lower.includes("walego")) return "🌊";
    if (lower.includes("tirana")) return "🇦🇱";
    if (lower.includes("jobego")) return "💼";
    return "🏢";
  };

  // Dynamic business unit display
  const buName = businessUnit?.name?.toLowerCase() || "team";
  const buEmoji = getBUEmoji(businessUnit?.name);

  return (
    <div className="bg-white h-full flex flex-col text-gray-900 overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-200 bg-white">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{buEmoji} {buName}</h1>
                <p className="text-sm text-gray-600 mt-1">{buName} team quarterly performance</p>
              </div>
              <select
                value={selectedCycleId || ""}
                onChange={(e) => setSelectedCycleId(e.target.value)}
                disabled={!cycles.length}
                className="bg-white border border-gray-300 rounded-lg pl-4 pr-10 py-2 text-sm text-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50">
                {cycles.length ? (
                  cycles.map((cycle) => (
                    <option key={cycle._id} value={cycle._id}>
                      {cycle.quarter} {cycle.year}
                    </option>
                  ))
                ) : (
                  <option value="">No cycle yet</option>
                )}
              </select>
            </div>
          </div>

        {/* Tabs Navigation */}
        <nav className="px-6 flex gap-1 border-b border-gray-200">
          {tabs.map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path}
              end={tab.end}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors ${
                  isActive
                    ? "bg-white text-blue-600 border-t-2 border-blue-600 -mb-px"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`
              }>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </NavLink>
          ))}
        </nav>
      </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-white">
          <Routes>
            <Route path="/" element={<Overview cycle={selectedCycle} />} />
            <Route path="/team" element={<TeamGrid cycle={selectedCycle} />} />
            <Route path="/assess" element={<Assessment cycle={selectedCycle} />} />
            <Route path="/assess/:userId" element={<Assessment cycle={selectedCycle} />} />
            <Route path="/settings" element={<Settings cycle={selectedCycle} fetchCycles={fetchCycles} />} />
          </Routes>
        </div>
      </div>
  );
}
